import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiArrowRight, FiCheck, FiMonitor, FiShoppingCart, FiSmartphone, FiSettings, FiTrendingUp } = FiIcons;

const WebDesignService = () => {
  const services = [
    {
      icon: FiMonitor,
      title: 'Custom Website Design',
      description: 'Bespoke website designs tailored to your brand identity and business objectives, ensuring a unique online presence.'
    },
    {
      icon: FiShoppingCart,
      title: 'E-commerce Solutions',
      description: 'Complete online store development with secure payment gateways, inventory management, and user-friendly shopping experiences.'
    },
    {
      icon: FiSmartphone,
      title: 'Mobile-Responsive Development',
      description: 'Websites that look and function perfectly across all devices, from desktop computers to mobile phones.'
    },
    {
      icon: FiSettings,
      title: 'CMS Integration',
      description: 'Easy-to-use content management systems that allow you to update your website content without technical knowledge.'
    },
    {
      icon: FiTrendingUp,
      title: 'SEO Optimization',
      description: 'Built-in search engine optimization to help your website rank higher and attract more organic traffic.'
    }
  ];

  const technologies = [
    {
      name: 'WordPress',
      description: 'Flexible CMS for content-rich websites'
    },
    {
      name: 'Shopify',
      description: 'Powerful e-commerce platform'
    },
    {
      name: 'Custom HTML/CSS',
      description: 'Bespoke development for unique needs'
    },
    {
      name: 'React/Next.js',
      description: 'Modern web applications'
    },
    {
      name: 'Mobile-First Design',
      description: 'Optimized for all devices'
    },
    {
      name: 'Performance Optimization',
      description: 'Fast loading and smooth user experience'
    }
  ];

  const packageFeatures = [
    'Custom website design and development',
    'Mobile-responsive layout',
    'Content management system',
    'Contact forms and lead capture',
    'Social media integration',
    'Basic SEO optimization',
    'Google Analytics setup',
    'SSL certificate and security',
    '3 months technical support',
    'Training and documentation'
  ];

  const process = [
    {
      step: '01',
      title: 'Planning & Strategy',
      description: 'We analyze your requirements, target audience, and business goals to create a comprehensive project plan.',
      timeline: '1 week'
    },
    {
      step: '02',
      title: 'Design & Wireframing',
      description: 'Create wireframes and visual designs that reflect your brand and provide excellent user experience.',
      timeline: '2-3 weeks'
    },
    {
      step: '03',
      title: 'Development & Coding',
      description: 'Build your website using modern technologies, ensuring performance, security, and scalability.',
      timeline: '3-4 weeks'
    },
    {
      step: '04',
      title: 'Testing & Optimization',
      description: 'Thorough testing across devices and browsers, plus performance optimization for fast loading.',
      timeline: '1 week'
    },
    {
      step: '05',
      title: 'Launch & Support',
      description: 'Deploy your website and provide training and ongoing support to ensure smooth operation.',
      timeline: '1 week'
    }
  ];

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="font-poppins font-bold text-4xl md:text-5xl lg:text-6xl mb-6">
              Web Design & <span className="text-purple-600">Development</span>
            </h1>
            <p className="font-inter text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-8">
              Build responsive, user-friendly websites that convert visitors into customers and drive business growth in the digital landscape.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-purple-600 text-white font-poppins font-semibold rounded-lg hover:bg-purple-700 transition-all duration-300 transform hover:scale-105"
              >
                Start Your Project <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/portfolio"
                className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-poppins font-semibold rounded-lg hover:bg-white hover:text-teal-600 transition-all duration-300"
              >
                View Our Work
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4">
              What We Offer
            </h2>
            <p className="font-inter text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive web design and development services that create engaging online experiences for your customers.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-lg card-hover border border-gray-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-teal-600 rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={service.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-poppins font-semibold text-xl text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="font-inter text-gray-600 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4">
              Technologies We Use
            </h2>
            <p className="font-inter text-lg text-gray-600 max-w-2xl mx-auto">
              We leverage the latest technologies and platforms to build websites that are fast, secure, and scalable.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {technologies.map((tech, index) => (
              <motion.div
                key={tech.name}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg p-6 shadow-md"
              >
                <h3 className="font-poppins font-semibold text-lg text-gray-900 mb-2">
                  {tech.name}
                </h3>
                <p className="font-inter text-gray-600 text-sm">
                  {tech.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4">
              Our Development Process
            </h2>
            <p className="font-inter text-lg text-gray-600 max-w-2xl mx-auto">
              Our systematic approach ensures your website is delivered on time, within budget, and exceeds your expectations.
            </p>
          </motion.div>
          <div className="space-y-8">
            {process.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`flex flex-col lg:flex-row items-center gap-8 ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className="flex-1">
                  <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-teal-600 rounded-lg flex items-center justify-center mr-4">
                        <span className="font-poppins font-bold text-white">{step.step}</span>
                      </div>
                      <div>
                        <h3 className="font-poppins font-semibold text-xl text-gray-900">
                          {step.title}
                        </h3>
                        <p className="font-inter text-purple-600 font-medium text-sm">
                          {step.timeline}
                        </p>
                      </div>
                    </div>
                    <p className="font-inter text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Package & Pricing */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-6">
                Complete Web Package
              </h2>
              <p className="font-inter text-lg text-gray-600 mb-8 leading-relaxed">
                Our comprehensive web design package includes everything you need to establish a professional online presence that drives results.
              </p>
              <div className="space-y-3 mb-8">
                {packageFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                    <span className="font-inter text-gray-600">{feature}</span>
                  </div>
                ))}
              </div>
              <div className="bg-gradient-to-r from-purple-600 to-teal-600 rounded-xl p-6 text-white mb-8">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-inter text-sm opacity-90">Starting Investment</p>
                    <p className="font-poppins font-bold text-3xl">From R25,000</p>
                  </div>
                  <div className="text-right">
                    <p className="font-inter text-sm opacity-90">Timeline</p>
                    <p className="font-poppins font-semibold text-lg">8-10 weeks</p>
                  </div>
                </div>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-purple-600 text-white font-poppins font-semibold rounded-lg hover:bg-purple-700 transition-all duration-300 transform hover:scale-105"
              >
                Get Started Today <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-w-4 aspect-h-3">
                <img
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop"
                  alt="Web development process"
                  className="w-full h-96 object-cover rounded-2xl shadow-xl"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-white rounded-2xl shadow-xl flex items-center justify-center">
                <div className="text-center">
                  <div className="font-poppins font-bold text-2xl text-purple-600">50+</div>
                  <div className="font-inter text-sm text-gray-600">Websites Built</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-4">
              Ready to Launch Your Website?
            </h2>
            <p className="font-inter text-lg text-gray-300 max-w-2xl mx-auto mb-8">
              Let's create a website that not only looks amazing but also drives real business results for your company.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-purple-600 text-white font-poppins font-semibold rounded-lg hover:bg-purple-700 transition-all duration-300 transform hover:scale-105"
              >
                Start Your Website Project <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/portfolio"
                className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-poppins font-semibold rounded-lg hover:bg-white hover:text-gray-900 transition-all duration-300"
              >
                See Our Portfolio
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WebDesignService;